import {
    Box,
    TextField,
    Accordion,
    AccordionSummary,
    Typography,
    AccordionDetails,
    Grid,
    FormGroup,
    FormControlLabel,
    Checkbox,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
} from "@mui/material";
import { Controller, FieldValues, Path, PathValue, useFormContext } from "react-hook-form";
import BuildingSelect from "../../../Autocomplete/BuildingSelect";
import CitiesSelect from "../../../Autocomplete/CitiesSelect";
import StreetSelect from "../../../Autocomplete/StreetSelect";
import CustomPasswordInput from "../../../UI/CustomPasswordInput";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useContext, useState } from "react";
import { MAIN_RULES } from "../../../../constants/formRules";
import { MainContextState, MainContext } from "../../../../context/MainContext";
import { dayArr } from "../../../../constants/common";

const addressInitialState = {
    cityId: "",
    streetId: "",
    zip: "",
};

interface IProps {
    checkedWeekend: Record<number, boolean>;
    setCheckedWeekend: (value: Record<number, boolean>) => void;
    editForm?: boolean;
}

export const CenterForm = <T extends FieldValues>({ editForm = false, checkedWeekend, setCheckedWeekend }: IProps) => {
    const { control, setValue, unregister } = useFormContext<T>();
    const [address, setAddress] = useState(addressInitialState);
    const { constants } = useContext<Partial<MainContextState>>(MainContext);

    return (
        <Box
            component="form"
            sx={{
                "& .MuiTextField-root": { my: 1 },
            }}
            noValidate
            autoComplete="off">
            <div>
                <Controller
                    defaultValue={"" as PathValue<T, Path<T>>}
                    name={"name" as Path<T>}
                    control={control}
                    rules={{
                        required: true,
                        maxLength: {
                            value: 120,
                            message: "Максимальная длина 120 символов",
                        },
                    }}
                    render={({ field, fieldState }) => {
                        return (
                            <TextField
                                {...field}
                                size={"small"}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                                label={"Наименование"}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        );
                    }}
                />
            </div>
            <div>
                <Controller
                    defaultValue={"" as PathValue<T, Path<T>>}
                    name={"phone" as Path<T>}
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            size={"small"}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                            label={"Телефон"}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
            </div>
            <div>
                <Controller
                    defaultValue={"" as PathValue<T, Path<T>>}
                    name={"email" as Path<T>}
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            size={"small"}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                            label={"Корп. почта"}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
            </div>
            {!editForm && (
                <>
                    <div>
                        <Controller
                            defaultValue={"" as PathValue<T, Path<T>>}
                            name={"manager.email" as Path<T>}
                            control={control}
                            rules={{ required: true }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    size={"small"}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    fullWidth
                                    label={"Почта ответств"}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                    </div>
                    <div>
                        <Controller
                            defaultValue={"" as PathValue<T, Path<T>>}
                            name={"manager.password" as Path<T>}
                            control={control}
                            rules={{ required: true }}
                            render={({ field, fieldState }) => (
                                <CustomPasswordInput
                                    {...field}
                                    size={"small"}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    fullWidth
                                    label={"Пароль ответств."}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                    </div>
                </>
            )}
            <div>
                <Controller
                    defaultValue={"" as PathValue<T, Path<T>>}
                    name={"chief_name" as Path<T>}
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            size={"small"}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                            label={"Руководитель"}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
            </div>
            <div>
                <Controller
                    defaultValue={"" as PathValue<T, Path<T>>}
                    name={"chief_work_position" as Path<T>}
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                        <TextField
                            {...field}
                            size={"small"}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            fullWidth
                            label={"Должность руководителя"}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                        />
                    )}
                />
            </div>
            {editForm && (
                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Статус</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Controller
                            defaultValue={"" as PathValue<T, Path<T>>}
                            name={"status.state" as Path<T>}
                            control={control}
                            render={({ field: { onChange, value }, fieldState }) => (
                                <FormControl sx={{ width: "100%", my: 1 }}>
                                    <InputLabel id={"center-state-label"}>Состояние статуса</InputLabel>
                                    <Select
                                        size={"small"}
                                        fullWidth
                                        labelId={"center-state-label"}
                                        label={"Состояние статуса"}
                                        onChange={(event: SelectChangeEvent) => {
                                            onChange(event.target.value);
                                        }}
                                        value={value}
                                        defaultValue={" "}
                                        autoComplete="off"
                                        error={!!fieldState.error}>
                                        {constants?.center_state.map(state => (
                                            <MenuItem key={state.value} value={state.value}>
                                                {state.text}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                                </FormControl>
                            )}
                        />
                        <Controller
                            defaultValue={"" as PathValue<T, Path<T>>}
                            name={"status.message" as Path<T>}
                            control={control}
                            rules={{
                                maxLength: MAIN_RULES.message,
                            }}
                            render={({ field, fieldState }) => (
                                <TextField
                                    {...field}
                                    size={"small"}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    fullWidth
                                    label={"Комментарий"}
                                    error={!!fieldState.error}
                                    helperText={fieldState.error?.message}
                                />
                            )}
                        />
                    </AccordionDetails>
                </Accordion>
            )}
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Адрес</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Controller
                        defaultValue={"" as PathValue<T, Path<T>>}
                        name={"address.city" as Path<T>}
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                            <CitiesSelect
                                onChange={onChange}
                                error={error}
                                value={value}
                                callbackResult={city => {
                                    setAddress({ ...address, cityId: city?.data?.fias_id });
                                    // setValue("address.coords", building.coords);
                                    // setValue("address.index", building.zip);
                                }}
                            />
                        )}
                    />
                    <Controller
                        defaultValue={"" as PathValue<T, Path<T>>}
                        name={"address.street" as Path<T>}
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                            <StreetSelect
                                onChange={onChange}
                                value={value}
                                error={error}
                                disabled={!address.cityId}
                                callbackResult={street => {
                                    setAddress({ ...address, streetId: street?.data?.fias_id });
                                }}
                                cityId={address.cityId}
                            />
                        )}
                    />
                    <Controller
                        defaultValue={"" as PathValue<T, Path<T>>}
                        name={"address.building" as Path<T>}
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                            <BuildingSelect
                                onChange={onChange}
                                value={value}
                                error={error}
                                disabled={!address.streetId}
                                callbackResult={building => {
                                    setAddress({ ...address, zip: building?.data?.postal_code ?? "" });
                                    setValue(
                                        "address.coords" as Path<T>,
                                        JSON.stringify([building?.data?.geo_lat, building?.data?.geo_lon]) as PathValue<
                                            T,
                                            Path<T>
                                        >,
                                    );
                                    setValue(
                                        "address.index" as Path<T>,
                                        (building?.data?.postal_code ?? "") as PathValue<T, Path<T>>,
                                    );
                                }}
                                streetId={address.streetId}
                            />
                        )}
                    />

                    <Controller
                        defaultValue={"" as PathValue<T, Path<T>>}
                        name={"address.index" as Path<T>}
                        control={control}
                        rules={{ required: true }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                size={"small"}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                                label={"Индекс"}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />
                </AccordionDetails>
            </Accordion>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>График работы</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    {dayArr.map((desc, index) => {
                        return (
                            <Grid container spacing={2} key={index}>
                                <Grid item xs={4}>
                                    <Typography
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            height: "100%",
                                        }}>
                                        {desc}
                                    </Typography>
                                </Grid>
                                <Grid item xs={2}>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={checkedWeekend[index]}
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                                        setCheckedWeekend({
                                                            ...checkedWeekend,
                                                            [index]: event.target.checked,
                                                        });

                                                        if (event.target.checked) {
                                                            unregister(`schedule.${index}.day` as Path<T>);
                                                            unregister(`schedule.${index}.from` as Path<T>);
                                                            unregister(`schedule.${index}.to` as Path<T>);
                                                            unregister(`schedule.${index}` as Path<T>);
                                                        } else {
                                                            // const values = Object.values(workDayIndex(checkedWeekend));
                                                            setValue(
                                                                // стейт не успевает обновиться поэтому + 1
                                                                `schedule.${index}.day` as Path<T>,
                                                                (index + 1) as PathValue<T, Path<T>>,
                                                            );
                                                        }
                                                    }}
                                                />
                                            }
                                            label="Выходной день"
                                        />
                                    </FormGroup>
                                </Grid>
                                {!checkedWeekend[index] && (
                                    <>
                                        <Grid item xs={3}>
                                            <Controller
                                                defaultValue={"" as PathValue<T, Path<T>>}
                                                name={`schedule.${index}.from` as Path<T>}
                                                control={control}
                                                rules={{ required: true }}
                                                render={({ field, fieldState }) => (
                                                    <TextField
                                                        {...field}
                                                        size={"small"}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                        type={"time"}
                                                        fullWidth
                                                        label={"c"}
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Controller
                                                defaultValue={"" as PathValue<T, Path<T>>}
                                                name={`schedule.${index}.to` as Path<T>}
                                                control={control}
                                                rules={{ required: true }}
                                                render={({ field, fieldState }) => (
                                                    <TextField
                                                        {...field}
                                                        size={"small"}
                                                        InputLabelProps={{
                                                            shrink: true,
                                                        }}
                                                        type={"time"}
                                                        fullWidth
                                                        label={"до"}
                                                        error={!!fieldState.error}
                                                        helperText={fieldState.error?.message}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        );
                    })}
                </AccordionDetails>
            </Accordion>
            <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>Юр. данные</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Controller
                        defaultValue={"" as PathValue<T, Path<T>>}
                        name={"company.name" as Path<T>}
                        control={control}
                        rules={{ required: true }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                size={"small"}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                                label={"Тип юр. лица"}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />
                    <Controller
                        defaultValue={"" as PathValue<T, Path<T>>}
                        name={"company.inn" as Path<T>}
                        control={control}
                        rules={{ required: true }}
                        render={({ field, fieldState }) => (
                            <TextField
                                {...field}
                                size={"small"}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                fullWidth
                                label={"ИНН"}
                                error={!!fieldState.error}
                                helperText={fieldState.error?.message}
                            />
                        )}
                    />
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};
